<template>
  <div class="cp-rd content">
    <PulseLoader
      :loading="setLoading"
      :class="{ hide: !setLoading }"
      quote="Action is the foundational key to all success"
      author="Pablo Picasso"
      profession="Spanish Painter"
      img="picasso.jpg"
    ></PulseLoader>

    <template v-if="ifInfo">
      <!-- page head -->
      <div class="cp-pagehead">
        <div>
          <h1 class="cp-title">Company Profile</h1>
          <p class="cp-sub">Identity, operations, and commerce records for a single company.</p>
        </div>
        <div class="cp-actions">
          <span class="cp-beta-tag"><md-icon>science</md-icon>Beta</span>
          <button class="cp-btn simple" :disabled="switchingClassic" @click="backToClassic">
            <md-icon>history</md-icon>{{ switchingClassic ? 'Switching…' : 'Back to classic view' }}
          </button>
          <button class="cp-btn simple" @click="feedback">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.8-.9L3 20.5l1.6-4.9A8.4 8.4 0 0 1 3.7 11 8.4 8.4 0 0 1 12 3a8.4 8.4 0 0 1 9 8.5z"/></svg>
            Feedback
          </button>
          <button class="cp-btn simple info" :disabled="!profileInfo" @click="downloadCSVData">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            {{ !profileInfo ? 'Loading…' : 'Download CSV' }}
          </button>
          <button class="cp-btn primary" :disabled="!profileInfo || pdfLoading" @click="downloadPDFData">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            {{ pdfLoading ? 'Downloading…' : 'Download PDF' }}
          </button>
        </div>
      </div>

      <!-- identity card -->
      <section class="cp-card cp-idcard">
        <div class="cp-idtop">
          <div>
            <h2 class="cp-name">{{ profileInfo.companyTitle }}</h2>
            <div class="cp-idline">
              <span class="bit" v-if="profileInfo.address">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 6-9 12-9 12s-9-6-9-12a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="2.8"/></svg>
                {{ profileInfo.address }}
              </span>
              <span class="sep" v-if="profileInfo.address && profileInfo.phone">·</span>
              <span class="bit" v-if="profileInfo.phone">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2 4.2 2 2 0 0 1 4 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8.1 9.7a16 16 0 0 0 6 6l1.2-1.1a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7A2 2 0 0 1 22 16.9z"/></svg>
                {{ profileInfo.phone }}
              </span>
            </div>
            <div class="cp-tags">
              <span class="cp-badge navy" v-if="ifInfo.sector || ifInfo.industry">
                {{ ifInfo.sector ? ifInfo.sector + ' → ' : '' }}{{ ifInfo.industry }}
              </span>
              <span class="cp-chip-code" v-if="classifications.primaryNAICS">
                <span class="lb">NAICS</span> {{ classifications.primaryNAICS }}
              </span>
              <span class="cp-chip-code" v-if="classifications.primarySIC">
                <span class="lb">SIC</span> {{ classifications.primarySIC }}
              </span>
            </div>
          </div>
        </div>
        <div class="cp-stats">
          <div class="cp-stat">
            <div class="k">Location sales</div>
            <div class="v">{{ dash(bizopStr('Location Sales')) }}</div>
            <div class="s">Estimated, this location</div>
          </div>
          <div class="cp-stat">
            <div class="k">Employment</div>
            <div class="v">{{ dash(bizopStr('Employment')) }}</div>
            <div class="s">This location</div>
          </div>
          <div class="cp-stat">
            <div class="k">In business since</div>
            <div class="v">{{ dash(companyDetails.estimatedOpenedForBusiness) }}</div>
            <div class="s">Estimated</div>
          </div>
          <div class="cp-stat">
            <div class="k">Value · sales</div>
            <div class="v">{{ dash(medianSales) }}</div>
            <div class="s">Industry median</div>
          </div>
          <div class="cp-stat">
            <div class="k">Value · earnings</div>
            <div class="v">{{ dash(medianEarnings) }}</div>
            <div class="s">Industry median</div>
          </div>
        </div>
      </section>

      <!-- tabs -->
      <div class="cp-tabcard">
        <div class="cp-tabbar" role="tablist">
          <button class="cp-tab" :class="{ on: activeTab === 't1' }" role="tab" @click="activeTab = 't1'">
            Company Details
          </button>
          <button class="cp-tab" :class="{ on: activeTab === 't2' }" role="tab" @click="activeTab = 't2'">
            Company Information
          </button>
          <button class="cp-tab" :class="{ on: activeTab === 't3' }" role="tab" @click="activeTab = 't3'">
            Commerce Details
          </button>
        </div>

        <!-- TAB 1 -->
        <div class="cp-panel" v-show="activeTab === 't1'">
          <div class="cp-grid g-25">
            <div class="cp-col">
              <section class="cp-card">
                <div class="cp-cardh"><span class="ico"><md-icon>work_outline</md-icon></span><h3>Business details</h3></div>
                <div class="cp-cardb"><dl class="cp-dl">
                  <div><dt>Legal name</dt><dd :class="{none:!isVal(companyDetails.legalName)}">{{ orNone(companyDetails.legalName) }}</dd></div>
                  <div><dt>Alternative name</dt><dd :class="{none:!isVal(companyDetails.alternativeName)}">{{ orNone(companyDetails.alternativeName) }}</dd></div>
                  <div><dt>In business since</dt><dd :class="{none:!isVal(companyDetails.estimatedOpenedForBusiness)}">{{ orNone(companyDetails.estimatedOpenedForBusiness) }}</dd></div>
                  <div><dt>Primary contact</dt><dd :class="{none:!isVal(profileInfo.businessOwner)}">{{ orNone(profileInfo.businessOwner) }}</dd></div>
                </dl></div>
              </section>

              <section class="cp-card">
                <div class="cp-cardh"><span class="ico"><md-icon>call</md-icon></span><h3>Contact</h3></div>
                <div class="cp-cardb"><dl class="cp-dl">
                  <div><dt>Main phone</dt><dd :class="{none:!isVal(companyDetails.phone)}">{{ orNone(companyDetails.phone) }}</dd></div>
                  <div><dt>Toll free</dt><dd :class="{none:!isVal(companyDetails.tollFreeNumber)}">{{ orNone(companyDetails.tollFreeNumber) }}</dd></div>
                  <div><dt>Additional phone</dt><dd :class="{none:!isVal(companyDetails.additionalPhone)}">{{ orNone(companyDetails.additionalPhone) }}</dd></div>
                  <div><dt>Fax</dt><dd :class="{none:!isVal(companyDetails.faxNumber)}">{{ orNone(companyDetails.faxNumber) }}</dd></div>
                </dl></div>
              </section>

              <section class="cp-card" v-if="operatingHoursAll.length">
                <div class="cp-cardh"><span class="ico"><md-icon>schedule</md-icon></span><h3>Operating hours</h3><span class="aside">Local time</span></div>
                <div class="cp-cardb"><dl class="cp-dl">
                  <div v-for="(h, i) in operatingHoursAll" :key="'h' + i">
                    <dt>{{ h['labels.days'] }}</dt>
                    <dd>{{ h['start_time'] }} – {{ h['end_time'] }}</dd>
                  </div>
                </dl></div>
              </section>

              <section class="cp-card">
                <div class="cp-cardh"><span class="ico sky"><md-icon>public</md-icon></span><h3>Web presence</h3></div>
                <div class="cp-cardb">
                  <dl class="cp-dl">
                    <div><dt>Website</dt>
                      <dd :class="{none:!isVal(webPresence.website)}">
                        <a v-if="isVal(webPresence.website)" :href="webUrl" target="_blank" rel="noopener">{{ webPresence.website }}</a>
                        <template v-else>Not reported</template>
                      </dd>
                    </div>
                    <div><dt>Site keywords</dt><dd :class="{none:!isVal(webPresence.websiteKeywords)}">{{ orNone(webPresence.websiteKeywords) }}</dd></div>
                    <div><dt>E-commerce</dt><dd :class="{none:!isVal(webPresence.hasEcommerce)}">{{ orNone(webPresence.hasEcommerce) }}</dd></div>
                  </dl>
                  <div class="cp-socials" v-if="socials.length">
                    <a class="cp-soc" v-for="s in socials" :key="s.name" :href="s.url" target="_blank" rel="noopener">
                      <i :class="s.icon"></i>{{ s.name }}
                    </a>
                  </div>
                </div>
              </section>

              <section class="cp-card" v-if="contacts && contacts.length">
                <div class="cp-cardh"><span class="ico"><md-icon>groups</md-icon></span><h3>People</h3><span class="aside">{{ contacts.length }} {{ contacts.length === 1 ? 'contact' : 'contacts' }}</span></div>
                <div class="cp-cardb"><div class="cp-people">
                  <div class="cp-person" v-for="(c, i) in contacts" :key="'c' + i">
                    <span class="cp-avatar">{{ initials(c.first_name, c.last_name) }}</span>
                    <div>
                      <div class="nm">{{ c.first_name }} {{ c.last_name }}</div>
                      <div class="ti">{{ c['labels.title_codes'] }}</div>
                    </div>
                  </div>
                </div></div>
              </section>
            </div>

            <div class="cp-col">
              <section class="cp-card">
                <div class="cp-cardh"><span class="ico sky"><md-icon>place</md-icon></span><h3>Location</h3></div>
                <div class="cp-map" v-if="!setLoading && hasLatLon">
                  <google-map-loader ref="mapRef" :locations="location" :center="mapCenter" />
                </div>
                <div class="cp-cardb pad" style="border-top:1px solid var(--cp-border-soft)"><dl class="cp-dl">
                  <div><dt>Address</dt><dd :class="{none:!isVal(profileInfo.address)}">{{ orNone(profileInfo.address) }}</dd></div>
                  <div><dt>Neighborhood</dt><dd :class="{none:!isVal(locationInfo.neighborhood)}">{{ orNone(locationInfo.neighborhood) }}</dd></div>
                </dl></div>
              </section>

              <section class="cp-card">
                <div class="cp-cardh"><span class="ico"><md-icon>attach_money</md-icon></span><h3>Company valuation</h3><span class="aside">Industry median</span></div>
                <div class="cp-cardb">
                  <dl class="cp-dl">
                    <div><dt>Sales-based multiple</dt><dd :class="{none:!isVal(medianSales)}">{{ orNone(medianSales) }}</dd></div>
                    <div><dt>Earnings-based multiple</dt><dd :class="{none:!isVal(medianEarnings)}">{{ orNone(medianEarnings) }}</dd></div>
                  </dl>
                  <div class="cp-note">
                    <md-icon>info_outline</md-icon>
                    <span>From DealStats transactions in this industry. Full quartile ranges live in <b>Bizminer Insights → Value Insights</b>.</span>
                  </div>
                </div>
              </section>

              <section class="cp-card">
                <div class="cp-cardh"><span class="ico"><md-icon>work_outline</md-icon></span><h3>Business operation</h3></div>
                <div class="cp-cardb"><dl class="cp-dl">
                  <div><dt>Location sales</dt><dd :class="{none:!isVal(bizopStr('Location Sales'))}">{{ orNone(bizopStr('Location Sales')) }}</dd></div>
                  <div><dt>Location employment</dt><dd :class="{none:!isVal(bizopStr('Employment'))}">{{ orNone(bizopStr('Employment')) }}</dd></div>
                  <div><dt>Corporate sales</dt><dd :class="{none:!isVal(bizopStr('Corporate Sales'))}">{{ orNone(bizopStr('Corporate Sales')) }}</dd></div>
                  <div><dt>Corporate employment</dt><dd :class="{none:!isVal(bizopStr('Corporate Employment'))}">{{ orNone(bizopStr('Corporate Employment')) }}</dd></div>
                </dl></div>
              </section>
            </div>
          </div>
        </div>

        <!-- TAB 2 -->
        <div class="cp-panel" v-show="activeTab === 't2'">
          <div class="cp-grid g-2">
            <div class="cp-col">
              <section class="cp-card">
                <div class="cp-cardh"><span class="ico"><md-icon>account_balance</md-icon></span><h3>Classification</h3></div>
                <div class="cp-cardb">
                  <dl class="cp-dl">
                    <div><dt>Sector</dt><dd :class="{none:!isVal(ifInfo.sector)}">{{ orNone(ifInfo.sector) }}</dd></div>
                    <div><dt>Industry</dt><dd :class="{none:!isVal(ifInfo.industry)}">{{ orNone(ifInfo.industry) }}</dd></div>
                    <div><dt>NAICS code</dt><dd class="mono" :class="{none:!isVal(classifications.primaryNAICS)}">{{ orNone(classifications.primaryNAICS) }}</dd></div>
                    <div><dt>SIC code</dt><dd class="mono" :class="{none:!isVal(classifications.primarySIC)}">{{ orNone(classifications.primarySIC) }}</dd></div>
                  </dl>
                  <template v-if="businessTypes.length">
                    <div class="cp-sublabel">Business types</div>
                    <div class="cp-pills"><span class="cp-pill navy" v-for="(t, i) in businessTypes" :key="'bt' + i">{{ t.label }}</span></div>
                  </template>
                </div>
              </section>

              <section class="cp-card">
                <div class="cp-cardh"><span class="ico"><md-icon>place</md-icon></span><h3>Location details</h3></div>
                <div class="cp-cardb"><dl class="cp-dl">
                  <div><dt>Neighborhood</dt><dd :class="{none:!isVal(locationInfo.neighborhood)}">{{ orNone(locationInfo.neighborhood) }}</dd></div>
                  <div><dt>Building number</dt><dd :class="{none:!isVal(locationDetails.buildingNumber)}">{{ orNone(locationDetails.buildingNumber) }}</dd></div>
                  <div><dt>Number of tenants</dt><dd :class="{none:!isVal(locationDetails.numberOfTenants)}">{{ orNone(locationDetails.numberOfTenants) }}</dd></div>
                  <div><dt>Landmark address</dt><dd :class="{none:!isVal(locationDetails.landmarkAddress)}">{{ orNone(locationDetails.landmarkAddress) }}</dd></div>
                </dl></div>
              </section>

              <section class="cp-card">
                <div class="cp-cardh"><span class="ico"><md-icon>markunread_mailbox</md-icon></span><h3>Mailing address</h3></div>
                <div class="cp-cardb"><dl class="cp-dl">
                  <div><dt>Address</dt><dd :class="{none:!isVal(mailing.mailingAddress)}">{{ orNone(mailing.mailingAddress) }}</dd></div>
                  <div><dt>State</dt><dd :class="{none:!isVal(mailing.mailingAddressState)}">{{ orNone(mailing.mailingAddressState) }}</dd></div>
                  <div><dt>ZIP</dt><dd class="mono" :class="{none:!isVal(mailingZip)}">{{ orNone(mailingZip) }}</dd></div>
                  <div><dt>Delivery point bar code</dt><dd class="mono" :class="{none:!isVal(mailing.deliveryPointBarCode)}">{{ orNone(mailing.deliveryPointBarCode) }}</dd></div>
                  <div><dt>Mailing DPBC</dt><dd class="mono" :class="{none:!isVal(mailing.mailingAddressDeliveryPointBarCode)}">{{ orNone(mailing.mailingAddressDeliveryPointBarCode) }}</dd></div>
                </dl></div>
              </section>
            </div>

            <div class="cp-col">
              <section class="cp-card">
                <div class="cp-cardh"><span class="ico"><md-icon>domain</md-icon></span><h3>Corporate details</h3></div>
                <div class="cp-cardb"><dl class="cp-dl">
                  <div><dt>Corporate headquarters</dt><dd :class="{none:!isVal(corporate.corporateHeadquarters)}">{{ orNone(corporate.corporateHeadquarters) }}</dd></div>
                  <div><dt>Location employee count</dt><dd :class="{none:!isVal(corporate.estimatedLocationEmployeeCount)}">{{ orNone(corporate.estimatedLocationEmployeeCount) }}</dd></div>
                  <div><dt>Corporate employee count</dt><dd :class="{none:!isVal(corporate.corporateEmployeeCount)}">{{ orNone(corporate.corporateEmployeeCount) }}</dd></div>
                  <div><dt>Ownership last changed</dt><dd :class="{none:!isVal(corporate.ownershipChangedOn)}">{{ orNone(corporate.ownershipChangedOn) }}</dd></div>
                  <div><dt>Fiscal year end</dt><dd :class="{none:!isVal(corporate.fiscalYearEndMonth)}">{{ orNone(corporate.fiscalYearEndMonth) }}</dd></div>
                  <div><dt>Corporate franchise</dt><dd :class="{none:!isVal(corporate.corporateFranchise)}">{{ orNone(corporate.corporateFranchise) }}</dd></div>
                  <div><dt>Franchise chain</dt><dd :class="{none:!isVal(corporate.franchiseChain)}">{{ orNone(corporate.franchiseChain) }}</dd></div>
                  <div><dt>Fortune 1000 ranking</dt><dd :class="{none:!isVal(corporate.fortunateRanking)}">{{ orNone(corporate.fortunateRanking) }}</dd></div>
                  <div><dt>Stock exchange</dt><dd :class="{none:!isVal(corporate.stockExchangeCode)}">{{ orNone(corporate.stockExchangeCode) }}</dd></div>
                  <div><dt>Ticker symbol</dt><dd :class="{none:!isVal(corporate.stockTickerSymbol)}">{{ orNone(corporate.stockTickerSymbol) }}</dd></div>
                </dl></div>
              </section>

              <section class="cp-card">
                <div class="cp-cardh"><span class="ico sky"><md-icon>public</md-icon></span><h3>Public information</h3></div>
                <div class="cp-cardb"><dl class="cp-dl">
                  <div><dt>Languages spoken</dt><dd :class="{none:!isVal(description.languagesSpoken)}">{{ orNone(description.languagesSpoken) }}</dd></div>
                  <div><dt>Public access</dt><dd :class="{none:!isVal(description.publicAccess)}">{{ orNone(description.publicAccess) }}</dd></div>
                  <div><dt>ATM available</dt><dd :class="{none:!isVal(description.shoppingCenterATM)}">{{ orNone(description.shoppingCenterATM) }}</dd></div>
                  <div><dt>Green score</dt><dd :class="{none:!isVal(description.greenScore)}">{{ orNone(description.greenScore) }}</dd></div>
                </dl></div>
              </section>

              <section class="cp-card" v-if="brands.length">
                <div class="cp-cardh"><span class="ico"><md-icon>sell</md-icon></span><h3>Brands</h3><span class="aside">{{ brands.length }}</span></div>
                <div class="cp-cardb"><div class="cp-pills"><span class="cp-pill" v-for="(b, i) in brands" :key="'br' + i">{{ b.label }}</span></div></div>
              </section>
            </div>
          </div>
        </div>

        <!-- TAB 3 -->
        <div class="cp-panel" v-show="activeTab === 't3'">
          <div class="cp-grid g-52">
            <div class="cp-col">
              <section class="cp-card">
                <div class="cp-cardh"><span class="ico"><md-icon>credit_card</md-icon></span><h3>Company credit</h3></div>
                <div class="cp-cardb">
                  <div class="cp-credit" v-if="isVal(credit.creditGrade) || isVal(credit.creditScore)">
                    <div class="cp-grade" :class="gradeClass(credit.creditGrade)" v-if="isVal(credit.creditGrade)">{{ credit.creditGrade }}</div>
                    <div class="cp-credit-m" v-if="isVal(credit.creditScore)">
                      <div class="lbl">Credit score</div>
                      <div class="sc"><b>{{ credit.creditScore }}</b><span v-if="scoreNum !== null">of 100</span></div>
                      <div class="meter" v-if="scoreNum !== null"><i :style="{ width: scoreNum + '%' }"></i></div>
                    </div>
                  </div>
                  <dl class="cp-dl">
                    <div><dt>Credit grade</dt><dd :class="{none:!isVal(credit.creditGrade)}">{{ orNone(credit.creditGrade) }}</dd></div>
                    <div><dt>Credit limit</dt><dd :class="{none:!isVal(credit.creditLimit)}">{{ orNone(credit.creditLimit) }}</dd></div>
                    <div><dt>Record created</dt><dd :class="{none:!isVal(credit.creditRecordCreatedDate)}">{{ orNone(credit.creditRecordCreatedDate) }}</dd></div>
                  </dl>
                </div>
              </section>

              <section class="cp-card">
                <div class="cp-cardh"><span class="ico"><md-icon>payments</md-icon></span><h3>Payment methods</h3></div>
                <div class="cp-cardb">
                  <template v-if="isVal(payment.paymentTypes)">
                    <div class="cp-sublabel">Accepted payment types</div>
                    <div class="cp-pills"><span class="cp-pill" v-for="(p, i) in paymentTypeList" :key="'pt' + i">{{ p }}</span></div>
                  </template>
                  <dl class="cp-dl" style="margin-top:6px">
                    <div><dt>Insurance accepted</dt><dd :class="{none:!isVal(payment.insurancesAccepted)}">{{ orNone(payment.insurancesAccepted) }}</dd></div>
                    <div><dt>Medicare accepted</dt><dd :class="{none:!isVal(payment.medicareAccepted)}">{{ orNone(payment.medicareAccepted) }}</dd></div>
                    <div><dt>Medicaid accepted</dt><dd :class="{none:!medicaidStr}">{{ medicaidStr || 'Not reported' }}</dd></div>
                  </dl>
                </div>
              </section>

              <section class="cp-card">
                <div class="cp-cardh"><span class="ico"><md-icon>gavel</md-icon></span><h3>Bankruptcy profile</h3><span class="cp-badge success" v-if="!bankruptcies.length">Clear</span></div>
                <div class="cp-cardb">
                  <div class="cp-empty" v-if="!bankruptcies.length">
                    <span class="ei"><md-icon>check</md-icon></span>
                    <div>
                      <div class="et">No bankruptcy filings on record</div>
                      <div class="ed">Nothing found in public bankruptcy records for this company.</div>
                    </div>
                  </div>
                  <template v-else>
                    <div class="cp-bk" v-for="(b, i) in bankruptcies" :key="'bk' + i">
                      <div class="cp-sublabel" v-if="bankruptcies.length > 1" style="padding-top:0">Filing {{ i + 1 }}</div>
                      <dl class="cp-dl">
                        <div><dt>Record created</dt><dd :class="{none:!isVal(b.bankruptcyRecordCreatedData)}">{{ orNone(b.bankruptcyRecordCreatedData) }}</dd></div>
                        <div><dt>Dismissed</dt><dd>{{ b.dismissal == 1 ? 'Yes' : 'No' }}</dd></div>
                        <div><dt>Filing date</dt><dd :class="{none:!isVal(b.filingDate)}">{{ orNone(b.filingDate) }}</dd></div>
                        <div><dt>Release date</dt><dd :class="{none:!isVal(b.releaseDate)}">{{ orNone(b.releaseDate) }}</dd></div>
                        <div><dt>Filing type</dt><dd :class="{none:!isVal(b.filingType)}">{{ orNone(b.filingType) }}</dd></div>
                      </dl>
                    </div>
                  </template>
                </div>
              </section>
            </div>

            <div class="cp-col">
              <section class="cp-card" v-if="benefitPlans.length">
                <div class="cp-cardh"><span class="ico"><md-icon>volunteer_activism</md-icon></span><h3>Benefit plans</h3><span class="aside">{{ benefitPlans.length }} {{ benefitPlans.length === 1 ? 'plan' : 'plans' }}</span></div>
                <div class="cp-cardb flush">
                  <div class="cp-tbl-wrap"><table class="cp-tbl">
                    <thead><tr><th>Plan</th><th>Carrier</th><th>Funding</th><th>Effective</th><th class="num">Participants</th></tr></thead>
                    <tbody>
                      <tr v-for="(p, i) in benefitPlans" :key="'bp' + i">
                        <td><b>{{ orNone(p.name) }}</b><div class="sub" v-if="isVal(p.contract_number)">Contract {{ p.contract_number }}</div></td>
                        <td>{{ orNone(p.carrier) }}<div class="sub" v-if="isVal(p['term.lower']) || isVal(p['term.upper'])">Term {{ p['term.lower'] }} – {{ p['term.upper'] }}</div></td>
                        <td>{{ orNone(p.funding_arrangements) }}</td>
                        <td>{{ orNone(p.effective_date) }}</td>
                        <td class="num">{{ participants(p) }}</td>
                      </tr>
                    </tbody>
                  </table></div>
                  <div class="cp-plan-toggle">
                    <button class="cp-btn simple sm" @click="planDetail = !planDetail">
                      <md-icon>{{ planDetail ? 'expand_less' : 'expand_more' }}</md-icon>
                      {{ planDetail ? 'Hide broker & filing detail' : 'Show broker & filing detail' }}
                    </button>
                  </div>
                  <div class="cp-plan-detail" v-show="planDetail">
                    <div class="cp-grid g-2" style="gap:14px">
                      <div v-for="(p, i) in benefitPlans" :key="'bpd' + i">
                        <div class="cp-sublabel" style="padding-top:0">{{ orNone(p.name) }}</div>
                        <dl class="cp-dl">
                          <div><dt>Broker</dt><dd :class="{none:!isVal(p.broker_name)}">{{ orNone(p.broker_name) }}</dd></div>
                          <div><dt>Broker location</dt><dd :class="{none:!brokerLoc(p)}">{{ brokerLoc(p) || 'Not reported' }}</dd></div>
                          <div><dt>Benefit arrangement</dt><dd :class="{none:!isVal(p.benefit_arrangements)}">{{ orNone(p.benefit_arrangements) }}</dd></div>
                          <div><dt>Welfare types</dt><dd :class="{none:!isVal(p.welfare_benefit_types)}">{{ orNone(p.welfare_benefit_types) }}</dd></div>
                          <div><dt>Short form filed</dt><dd :class="{none:!isVal(p.short_form)}">{{ orNone(p.short_form) }}</dd></div>
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section class="cp-card" v-else>
                <div class="cp-cardh"><span class="ico"><md-icon>volunteer_activism</md-icon></span><h3>Benefit plans</h3></div>
                <div class="cp-cardb"><div class="cp-empty">
                  <span class="ei neutral"><md-icon>info_outline</md-icon></span>
                  <div><div class="et">No benefit-plan filings on record</div><div class="ed">No Form 5500 benefit-plan records were found for this company.</div></div>
                </div></div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- feedback modal (same behavior as classic CPHeader) -->
    <modal v-if="feedbackModal" @close="feedbackModal = false" class="feedback_modal">
      <template slot="body">
        <button type="button" class="close_btn" @click="feedbackModal = false"><md-icon>clear</md-icon></button>
        <h3 class="modal-title">Have feedback on this product?</h3>
        <p>Enter the details below to send your feedback to our Product Team.</p>
        <form action class="form_grp">
          <div class="field_wpr">
            <label>Leave A Feedback Note</label>
            <textarea v-model="textFeedback" placeholder="Type a message"></textarea>
            <md-field>
              <md-select v-model="category" name="category">
                <md-option v-for="(option, key) in optionsCategory" :key="key" :label="option" :value="key">{{ option }}</md-option>
              </md-select>
            </md-field>
            <md-button @click="sendFeedback" class="md-info md-round mt-4">Send Feedback</md-button>
          </div>
        </form>
      </template>
    </modal>

    <!-- hidden PDF renderer (same ref/props as classic) -->
    <div v-if="user && ifInfo" style="display:none">
      <PdfDocument ref="cppdf" :headerInfo="{ headerData: ifInfo, user }" />
    </div>
  </div>
</template>

<script>
import { PulseLoader, Modal } from '@/components';
import GoogleMapLoader from '@/pages/Dashboard/Components/GoogleMapLoader';
import { mapState, mapActions } from 'vuex';
import { posthog } from '@/main';
import PdfDocument from './_partials/PdfDocument.vue';
import { setCompanyProfileVariant, reloadWithProfileVariant } from './profileFlag';
import axios from 'axios';

const EMPTY = {};

export default {
  name: 'company-profile-redesign',
  components: { PulseLoader, Modal, GoogleMapLoader, PdfDocument },
  data() {
    return {
      activeTab: 't1',
      planDetail: false,
      // feedback (mirrors CPHeader)
      optionsCategory: {
        0: 'Select a Category',
        1: '1 - Report an Error',
        2: '2 - Suggest an Improvement',
        3: '3 - Request a Correction',
        4: '4 - Request an Update',
      },
      category: 0,
      feedbackModal: false,
      textFeedback: '',
      switchingClassic: false,
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.user.user,
      pdfLoading: (state) => state.user.pdfLoading,
    }),
    ifInfo() {
      return this.$store.getters.profileInfo;
    },
    profileInfo() {
      return this.$store.getters.profileInfo;
    },
    setLoading() {
      return !this.profileInfo;
    },
    // ---- expanded sub-objects (guarded) ----
    expanded() {
      return (this.ifInfo && this.ifInfo.expanded) || EMPTY;
    },
    companyDetails() {
      return this.expanded.companyDetails || EMPTY;
    },
    classifications() {
      return this.expanded.companyClassifications || EMPTY;
    },
    businessTypes() {
      return (this.classifications && this.classifications.businessTypes) || [];
    },
    description() {
      return this.expanded.companyDescription || EMPTY;
    },
    mailing() {
      return this.expanded.mailingAddress || EMPTY;
    },
    mailingZip() {
      const z = this.mailing.mailingAddressZip;
      const four = this.mailing.mailingAddressZipFour;
      if (!z) return '';
      return four ? `${z}-${four}` : `${z}`;
    },
    locationInfo() {
      return this.expanded.location || EMPTY;
    },
    locationDetails() {
      return this.expanded.locationDetails || EMPTY;
    },
    corporate() {
      return this.expanded.corporateDetails || EMPTY;
    },
    webPresence() {
      return this.expanded.webPresense || EMPTY; // note: source key is misspelled "webPresense"
    },
    webUrl() {
      const w = this.webPresence.website || '';
      if (!w) return '#';
      return /^https?:\/\//i.test(w) ? w : 'https://' + w;
    },
    credit() {
      return this.expanded.credit || EMPTY;
    },
    scoreNum() {
      const n = parseFloat(this.credit.creditScore);
      if (isNaN(n)) return null;
      return Math.max(0, Math.min(100, n));
    },
    payment() {
      return this.expanded.paymentMethods || EMPTY;
    },
    paymentTypeList() {
      const p = this.payment.paymentTypes;
      if (Array.isArray(p)) return p;
      if (typeof p === 'string') return p.split(',').map((x) => x.trim()).filter(Boolean);
      return [];
    },
    medicaidStr() {
      const m = this.payment.medicaidAccepted;
      if (Array.isArray(m)) return m.join(', ');
      return m || '';
    },
    bankruptcies() {
      const b = this.expanded.bankruptcy;
      return Array.isArray(b) ? b : b ? Object.values(b) : [];
    },
    benefitPlans() {
      const b = this.expanded.benefitsPlans;
      return Array.isArray(b) ? b : b ? Object.values(b) : [];
    },
    brands() {
      const b = this.expanded.brandes; // note: source key is misspelled "brandes"
      return (b && b.brandList) || [];
    },
    medianSales() {
      try { return this.ifInfo.multiples.data.Median.sales; } catch (e) { return ''; }
    },
    medianEarnings() {
      try { return this.ifInfo.multiples.data.Median.earnings; } catch (e) { return ''; }
    },
    // ---- fine details ----
    contacts() {
      return (this.ifInfo && this.ifInfo.fine_details && this.ifInfo.fine_details.contacts) || [];
    },
    operatingHoursAll() {
      const h = this.ifInfo && this.ifInfo.fine_details && this.ifInfo.fine_details.hours;
      if (!Array.isArray(h)) return [];
      return h.filter((row) => row && row['start_time']);
    },
    socials() {
      const socials = [];
      const details = (this.ifInfo && this.ifInfo.fine_details && this.ifInfo.fine_details.socials) || EMPTY;
      if (details.facebook) socials.push({ name: 'Facebook', url: details.facebook, icon: 'fab fa-facebook' });
      if (details.foursquare) socials.push({ name: 'FourSquare', url: details.foursquare, icon: 'fab fa-foursquare' });
      if (details.instagram) socials.push({ name: 'Instagram', url: details.instagram, icon: 'fab fa-instagram' });
      if (details.linked_in) socials.push({ name: 'LinkedIn', url: details.linked_in, icon: 'fab fa-linkedin' });
      if (details.pinterest) socials.push({ name: 'Pinterest', url: details.pinterest, icon: 'fab fa-pinterest' });
      if (details.tumblr) socials.push({ name: 'Tumblr', url: details.tumblr, icon: 'fab fa-tumblr' });
      if (details.twitter) socials.push({ name: 'Twitter', url: details.twitter, icon: 'fab fa-twitter' });
      if (details.yelp) socials.push({ name: 'Yelp', url: details.yelp, icon: 'fab fa-yelp' });
      if (details.youtube) socials.push({ name: 'YouTube', url: details.youtube, icon: 'fab fa-youtube' });
      return socials;
    },
    // ---- map ----
    hasLatLon() {
      return !!(this.profileInfo && this.profileInfo.latlon && this.profileInfo.latlon.lat);
    },
    location() {
      if (!this.hasLatLon) return [];
      return [{ CONAME: this.profileInfo.companyTitle, lat: this.profileInfo.latlon.lat, lng: this.profileInfo.latlon.lon }];
    },
    mapCenter() {
      return this.hasLatLon ? { lat: this.profileInfo.latlon.lat, lon: this.profileInfo.latlon.lon } : null;
    },
    // ---- CSV (identical column set + values to classic CompanyProfile.vue) ----
    getFormattedData() {
      const a = {};
      const p = this.profileInfo;
      a.company = p.companyTitle;
      a.original_name = '';
      a.infogroup = p.locnum;
      a.unique_id = '';
      a.street = p.street ? p.street : '';
      a.city = p.city ? p.city : '';
      a.state = p.state ? p.state : '';
      a.zip = p.zip ? p.zip : '';
      a.industry_code = p.naics;
      a.industry = p.industry;
      const b = p.businessOwner ? p.businessOwner.split(',')[0] : '';
      a.contact_first = b.split(' ')[0];
      a.contact_last = b.split(' ')[1];
      a.phone = p.businessPhone ? p.businessPhone : '';
      a.url = p.businessURL ? p.businessURL : '';
      const g = p.release_id.replace('_', '-01-20');
      const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      a.operation_details = monthNames[new Date(g).getMonth()] + ' ' + new Date(g).getFullYear();
      a.location_annual_sales = p.bizop['Location Sales'][0];
      a.location_employment = p.bizop['Employment'][0];
      a.corporation_annual_sales = p.bizop['Corporate Sales'][0];
      a.corporation_employment = p.bizop['Corporate Employment'][0];
      a.market_insights_date = monthNames[new Date(g).getMonth()] + ' ' + new Date(g).getFullYear();
      a.market_industry_code = p.IMRnaics;
      a.market_industry = p.IMRindustry;
      a.market_area = p['MR']['area'];
      a.industry_operations = p['MR']['main']['Industry Operations'];
      a.location_sales_rank = p['MR']['main']['Business Sales Rank'];
      a.industry_market_sales_volume = p['MR']['main']['Annual Market Sales Volume'];
      a.average_site_sales = p['MR']['main']['Average Site Sales'];
      a.average_small_business_sales = p['MR']['main']['Average Small Business Sales'];
      a.industry_market_sales_per_person = p['MR']['penetration']['Market Sales per Person'];
      // penetration is a sparse map, not a fixed schema: the API omits the US
      // Sales / Opportunity Index keys on ALL US reports, and drops the two
      // "US Sales per ..." keys for NAICS 21/31/32/33 (Mining, Manufacturing)
      // at any market area. Unguarded reads yielded undefined, which join()
      // turned into silently blank CSV cells. "N/A" matches what the API
      // already emits for absent Sales/Earnings Approach values in this row.
      a.us_industry_sales_per_person = p['MR']['penetration']['US Sales per Person'] ?? 'N/A';
      a.opportunity_index_person = p['MR']['penetration']['Opportunity Index Person'] ?? 'N/A';
      a.industry_market_sales_per_household = p['MR']['penetration']['Market Sales per Household'];
      a.us_industry_sales_per_household = p['MR']['penetration']['US Sales per Household'] ?? 'N/A';
      a.opportunity_index_household = p['MR']['penetration']['Opportunity Index Household'] ?? 'N/A';
      a.market_area_population = p['MR']['income']['Population'];
      a.market_area_households = p['MR']['income']['Households'];
      a.per_capita_income = p['MR']['income']['Per Capita Income'];
      a.mediab_hh__income = p['MR']['income']['Median HH Income'];
      a.business_values_insights_date = monthNames[new Date(g).getMonth()] + ' ' + new Date(g).getFullYear();
      a.sales_approach_quartile1 = p.multiples.data['Quartile 1']['sales'];
      a.sales_approach_median = p.multiples.data['Median']['sales'];
      a.sales_approach_quartile3 = p.multiples.data['Quartile 3']['sales'];
      a.earnings_approach_quartile1 = p.multiples.data['Quartile 1']['earnings'];
      a.earnings_approach_median = p.multiples.data['Median']['earnings'];
      a.earnings_approach_quartile3 = p.multiples.data['Quartile 3']['earnings'];
      a.if_industry_code = p.naics;
      a.if_industry_name = p.industry;
      a.if_industry_sales_class = p.IF['sales-class'];
      a.financial_insights_date = p.IF['period'];
      a.gross_margin_percentage = p.IF.spend['Gross Margin'].percentage;
      a.office_owner_compensation_percentage = p.IF.spend['Officer-Owner-Compensation'].percentage;
      a.depreciation_percentage = p.IF.spend['Depreciation'].percentage;
      a.operating_expense_percentage = p.IF.spend['Operating Expenses'].percentage;
      a.operating_income_percentage = p.IF.spend['Operating Income'].percentage;
      a.after_tax_net_profit_percentage = p.IF.spend['After Tax Net Profit'].percentage;
      a.owner_earnings_percentage = p.IF.spend['Owner Earnings'].percentage;
      a.account_receivable_percentage = p.IF.spend['Accounts Receivable: Total Assets'].percentage;
      a.account_payable_percentage = p.IF.spend['Accounts Payable: TLNW'].percentage;
      a.net_worth_assets_percentage = p.IF.spend['Net Worth: Assets'].percentage;
      a.gross_margin_dollar = p.IF.spend['Gross Margin'].dollar;
      a.office_owner_compensation_dollar = p.IF.spend['Officer-Owner-Compensation'].dollar;
      a.depreciation_dollar = p.IF.spend['Depreciation'].dollar;
      a.operating_expense_dollar = p.IF.spend['Operating Expenses'].dollar;
      a.operating_income_dollar = p.IF.spend['Operating Income'].dollar;
      a.after_tax_net_profit_dollar = p.IF.spend['After Tax Net Profit'].dollar;
      a.owner_earnings_dollar = p.IF.spend['Owner Earnings'].dollar;
      a.account_receivable_dollar = p.IF.spend['Accounts Receivable: Total Assets'].dollar;
      a.account_payable_dollar = p.IF.spend['Accounts Payable: TLNW'].dollar;
      a.net_worth_assets_dollar = p.IF.spend['Net Worth: Assets'].dollar;
      a.account_payable_revenue = p.IF.benchmarks['Accounts Payable: Revenue'];
      a.current_ratio = p.IF.benchmarks['Current Ratio'];
      a.days_payable = p.IF.benchmarks['Days Payable'];
      a.net_working_capital_revenue = p.IF.benchmarks['Net Working Capital Revenue'];
      a.days_receivable = p.IF.benchmarks['Days Receivable'];
      a.interest_coverage = p.IF.benchmarks['Interest Coverage'];
      a.current_liabilites = p.IF.benchmarks['Current Liabilities: NW'];
      a.loans_notes = p.IF.benchmarks['Loans‐Notes Payable: Net Worth'];
      a.modified_z = p.IF.benchmarks['Modified Z Score'];
      a.total_liabilities = p.IF.benchmarks['Total Liabilties: Net Worth'];

      const list = [];
      Object.values(a).forEach((element) => {
        let v = element;
        if (element && element.includes && element.includes(',')) v = element.replace(/,/g, '.');
        list.push(v);
      });
      return list;
    },
    getCsvData() {
      const csvData = [[
        'Company','Original Name','Infogroup ID','Unique ID','Street','City','State','Zip','Industry Code','Industry',
        'Contact First','Contact Last','Phone','URL','Operation Detail Release','Location Annual Sales','Location Employment',
        'Corporate Annual Sales','Corporate Employment','Market Insights Date','Market Industry Code','Market Industry','Market Area',
        'Industry Operations','Location Sales Rank','Industry Market Sales Volume','Average Site Sales','Average Small Business Sales',
        'Industry Market Sales per Person','US Industry Sales per Person','Opportunity Index','Industry Market Sales per Household',
        'US Industry Sales per Household','Opportunity Index','Market Area Population','Market Area Households','Per Capital Income',
        'Median Household Income','Business Value Insights Date','Sales Approach Quartile1','Sales Approach Median','Sales Approach Quartile3',
        'Earnings Approach Quartile1','Earnings Approach Median','Earnings Approach Quartile3','IF Industry code','IF Industry name',
        'IF Sales class','Financial Insights Date','Gross Margin %','Officer-Owner Compensation %','Depreciation %','Operating Expense %',
        'Operating Income %','After Tax Net Profit %','Owner Earnings %','Accounts Receivable/Total Assets %','Accounts Payable/TLNW %',
        'Net Worth/Assets %','Gross Margin $','Officer-Owner Compensation $','Depreciation $','Operating Expense $','Operating Income $',
        'After Tax Net Profit $','Owner Earnings $','Accounts Receivable/Total Assets $','Accounts Payable/TLNW $','Net Worth/Assets $',
        'Accounts Payable/Revenue','Current Ratio','Days Payable','Net Working Capital/Revenue','Days Receivable','Interest Coverage',
        'Current Liabilities/Net Worth','Loans Notes Payable/Net Worth','Modified Z Score','Total Liabilties/Net Worth',
      ]];
      csvData.push(this.getFormattedData);
      return csvData;
    },
  },
  mounted() {
    const info = this.$store.getters.profileInfo;
    posthog.capture('company_profile_viewed', {
      company_id: this.$route.params.id,
      company_name: info && info.companyTitle,
      industry: info && info.naics,
      ui: 'redesign',
    });
    this.$store.commit('user/SET_PDF_LOADING', false);
  },
  methods: {
    ...mapActions({ registerProductExport: 'tracking/registerProductExport' }),
    async backToClassic() {
      this.switchingClassic = true;
      // wait until the flag actually serves 'classic' before reloading, so the
      // gate doesn't read the stale cached variant and keep the user on beta
      await setCompanyProfileVariant('classic');
      setTimeout(reloadWithProfileVariant, 400);
    },
    isVal(v) {
      return v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
    },
    orNone(v) {
      return this.isVal(v) ? v : 'Not reported';
    },
    dash(v) {
      return this.isVal(v) ? v : '—';
    },
    bizopStr(key) {
      try {
        const val = this.ifInfo.bizop[key];
        return val && val.toString ? val.toString() : val;
      } catch (e) {
        return '';
      }
    },
    initials(a, b) {
      return ((a || '').charAt(0) + (b || '').charAt(0)).toUpperCase() || '—';
    },
    gradeClass(g) {
      const c = (g || '').toString().trim().charAt(0).toUpperCase();
      if (c === 'A' || c === 'B') return 'good';
      if (c === 'C') return 'warn';
      if (c === 'D' || c === 'E' || c === 'F') return 'bad';
      return '';
    },
    participants(p) {
      const beg = p.active_beginning_participants;
      const end = p.active_ending_participants;
      if (this.isVal(beg) && this.isVal(end)) return `${beg} → ${end}`;
      if (this.isVal(beg)) return `${beg}`;
      if (this.isVal(p.total_beginning_participants)) return `${p.total_beginning_participants}`;
      return '—';
    },
    brokerLoc(p) {
      const parts = [p.broker_city, p.broker_state, p.broker_postal_code].filter(Boolean);
      return parts.join(', ');
    },
    // ---- exports / feedback (behavior identical to classic CPHeader) ----
    downloadPDFData() {
      this.$refs.cppdf.generateReport();
      this.registerProductExport({
        userId: this.user && this.user.userID,
        productId: this.$route.params.id,
        exportType: 'PDF',
      });
    },
    downloadCSVData() {
      let csv = '';
      this.getCsvData.forEach((row) => {
        csv += row.join(',');
        csv += '\n';
      });
      const anchor = document.createElement('a');
      anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
      anchor.target = '_blank';
      anchor.download = 'Company Profile.csv';
      anchor.click();
      this.registerProductExport({
        userId: this.user && this.user.userID,
        productId: this.$route.params.id,
        exportType: 'CSV',
      });
    },
    feedback() {
      this.feedbackModal = true;
    },
    sendFeedback() {
      let parameters = {};
      parameters['name'] = 'Company Profile';
      parameters['Industry'] = this.ifInfo;
      parameters = JSON.stringify(parameters);
      if (this.category) {
        const currentSession = localStorage.getItem('vue-authenticate.vueauth_access_token');
        axios
          .post(process.env.VUE_APP_API_BASE_URL + '/user/feedback/submit', {}, {
            params: {
              session_id: currentSession,
              feedback: this.textFeedback,
              url: `${window.location.href}`,
              category: this.category,
              parameters: parameters,
            },
          })
          .then((res) => {
            if (res.hasOwnProperty('data') && res.data.error) {
              this.$store.commit('SET_ERROR_BIT', true);
              this.$store.commit('SET_ERROR_INFO',
                '<br>Server responded with error = True <br> errorCode: ' + res.data.errorCode +
                `<br> ${process.env.VUE_APP_API_BASE_URL} + "/user/feedback/submit" <br>`);
              throw new Error('Server responded with error = True, errorCode: ' + res.data.errorCode);
            }
            if (res.data.success) this.$store.dispatch('alerts/success', 'Successfully sent');
            else this.$store.dispatch('alerts/error', 'An error occured. please try again');
            this.feedbackModal = false;
            this.textFeedback = null;
            this.category = null;
          });
      } else {
        this.$store.commit('SET_ERROR_BIT', true);
        this.$store.commit('SET_ERROR_INFO',
          '<br>Server responded with error = True <br> errorCode: category_not_set' +
          `<br> ${process.env.VUE_APP_API_BASE_URL} + "/user/feedback/submit" <br>`);
      }
    },
  },
};
</script>

<!-- map height override (unscoped; unique class so it can't leak elsewhere) -->
<style>
.cp-rd .cp-map #google-map {
  height: 300px !important;
}
</style>

<style scoped>
.cp-rd {
  --cp-navy: #003666;
  --cp-navy-ink: #002a52;
  --cp-sky: #0cbcf2;
  --cp-sky-dark: #0a9fce;
  --cp-green: #2d6731;
  --cp-green-bg: #e7f0e8;
  --cp-green-br: #cfe3d1;
  --cp-amber: #b26a00;
  --cp-amber-bg: #fff3e0;
  --cp-red: #b3372c;
  --cp-red-bg: #f9ecea;
  --cp-red-br: #e6c3bd;
  --cp-text: #3c4858;
  --cp-muted: #6d718e;
  --cp-faint: #9299a6;
  --cp-border: #d9d9d9;
  --cp-border-soft: #e6e7e9;
  --cp-surface: #ffffff;
  --cp-surface-2: #f5f6f7;
  --cp-navy-wash: #e8eef4;
  --cp-navy-wash-br: #cdd9e6;
  --cp-sky-wash: #e5f6fd;
  --cp-sky-wash-br: #bde8f8;
  --cp-radius: 6px;
  --cp-shadow: 0 1px 4px rgba(0, 0, 0, 0.14);
  --cp-sans: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  --cp-mono: ui-monospace, 'SF Mono', Menlo, Consolas, monospace;
  font-family: var(--cp-sans);
  color: var(--cp-text);
  font-size: 14px;
  line-height: 1.5;
}
.cp-rd *,
.cp-rd *::before,
.cp-rd *::after { box-sizing: border-box; }

/* page head */
.cp-pagehead { display: flex; align-items: flex-start; justify-content: space-between; gap: 22px; flex-wrap: wrap; margin-bottom: 18px; }
.cp-title { font-size: 33px; line-height: 40px; font-weight: 500; color: var(--cp-navy); letter-spacing: -0.01em; margin: 0; }
.cp-sub { margin: 4px 0 0; font-size: 13.5px; color: var(--cp-muted); }
.cp-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.cp-beta-tag { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 99px; background: var(--cp-navy-wash); color: var(--cp-navy); font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; border: 1px solid var(--cp-navy-wash-br); }
.cp-beta-tag .md-icon { color: var(--cp-navy) !important; font-size: 14px !important; }
.cp-btn.simple .md-icon { color: inherit !important; font-size: 16px !important; }

/* buttons */
.cp-btn { display: inline-flex; align-items: center; gap: 7px; text-transform: uppercase; font-size: 12px; font-weight: 500; letter-spacing: 0.03em; border: none; border-radius: 3px; padding: 9px 16px; cursor: pointer; transition: box-shadow 0.2s, background-color 0.2s, color 0.2s; color: #fff; white-space: nowrap; font-family: var(--cp-sans); }
.cp-btn svg { width: 15px; height: 15px; }
.cp-btn.primary { background: var(--cp-navy); box-shadow: 0 2px 5px rgba(0, 54, 102, 0.28); }
.cp-btn.primary:hover:not(:disabled) { background: var(--cp-navy-ink); box-shadow: 0 4px 10px rgba(0, 54, 102, 0.34); }
.cp-btn.simple { background: transparent; color: var(--cp-navy); box-shadow: none; }
.cp-btn.simple:hover:not(:disabled) { background: var(--cp-navy-wash); }
.cp-btn.simple.info { color: var(--cp-sky-dark); }
.cp-btn.simple.info:hover:not(:disabled) { background: var(--cp-sky-wash); }
.cp-btn.sm { padding: 6px 12px; font-size: 11px; }
.cp-btn:disabled { opacity: 0.55; cursor: not-allowed; box-shadow: none; }

/* cards */
.cp-card { background: var(--cp-surface); border-radius: var(--cp-radius); box-shadow: var(--cp-shadow); overflow: hidden; }
.cp-cardh { display: flex; align-items: center; gap: 11px; padding: 13px 20px; border-bottom: 1px solid var(--cp-border-soft); }
.cp-cardh .ico { width: 30px; height: 30px; border-radius: 5px; background: var(--cp-navy-wash); color: var(--cp-navy); display: flex; align-items: center; justify-content: center; flex: none; }
.cp-cardh .ico.sky { background: var(--cp-sky-wash); color: var(--cp-sky-dark); }
.cp-cardh .ico .md-icon { color: inherit !important; font-size: 18px !important; }
.cp-cardh h3 { margin: 0; font-size: 14.5px; font-weight: 600; letter-spacing: -0.005em; flex: 1; color: var(--cp-text); }
.cp-cardh .aside { font-size: 11.5px; color: var(--cp-faint); font-weight: 500; }
.cp-cardb { padding: 6px 20px 14px; }
.cp-cardb.pad { padding: 15px 20px; }
.cp-cardb.flush { padding: 0; }

/* identity */
.cp-idcard { margin-bottom: 18px; }
.cp-idtop { display: flex; align-items: flex-start; justify-content: space-between; gap: 22px; flex-wrap: wrap; padding: 18px 20px 16px; }
.cp-name { margin: 0; font-size: 23px; line-height: 28px; font-weight: 600; color: var(--cp-navy); letter-spacing: -0.01em; }
.cp-idline { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; margin-top: 9px; font-size: 13px; color: var(--cp-muted); }
.cp-idline .bit { display: inline-flex; align-items: center; gap: 6px; }
.cp-idline .bit svg { color: var(--cp-faint); }
.cp-idline .sep { color: var(--cp-border); }
.cp-tags { display: flex; align-items: center; gap: 7px; flex-wrap: wrap; margin-top: 12px; }

/* badges */
.cp-badge { display: inline-flex; align-items: center; gap: 4px; font-size: 10.5px; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; padding: 4px 9px; border-radius: 12px; background: var(--cp-surface-2); color: var(--cp-muted); border: 1px solid var(--cp-border-soft); }
.cp-badge.success { background: var(--cp-green-bg); color: var(--cp-green); border-color: var(--cp-green-br); }
.cp-badge.navy { background: var(--cp-navy-wash); color: var(--cp-navy); border-color: var(--cp-navy-wash-br); }
.cp-chip-code { display: inline-flex; align-items: center; gap: 5px; font-family: var(--cp-mono); font-size: 11px; font-weight: 500; padding: 3px 9px; border-radius: 12px; background: var(--cp-sky-wash); color: var(--cp-navy); border: 1px solid var(--cp-sky-wash-br); }
.cp-chip-code .lb { font-family: var(--cp-sans); font-weight: 600; color: var(--cp-sky-dark); }

/* stat strip */
.cp-stats { display: grid; grid-template-columns: repeat(5, 1fr); border-top: 1px solid var(--cp-border-soft); }
.cp-stat { padding: 13px 20px; border-right: 1px solid var(--cp-border-soft); }
.cp-stat:last-child { border-right: none; }
.cp-stat .k { font-size: 10px; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; color: var(--cp-faint); }
.cp-stat .v { margin-top: 5px; font-size: 17px; font-weight: 600; color: var(--cp-navy); font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
.cp-stat .s { margin-top: 2px; font-size: 11.5px; color: var(--cp-muted); }
@media (max-width: 1080px) { .cp-stats { grid-template-columns: repeat(3, 1fr); } .cp-stat { border-bottom: 1px solid var(--cp-border-soft); } }
@media (max-width: 620px) { .cp-stats { grid-template-columns: repeat(2, 1fr); } }

/* tabs */
.cp-tabcard { margin-bottom: 4px; }
.cp-tabbar { background: #e3e4e5; border-radius: 5px; display: flex; padding: 6px; gap: 5px; box-shadow: 0 2px 8px rgba(0, 0, 0, 0.13); }
.cp-tab { flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px; padding: 11px 14px; font-size: 12px; text-transform: uppercase; font-weight: 500; letter-spacing: 0.03em; color: #5a5f67; border: none; background: transparent; border-radius: 3px; cursor: pointer; transition: background 0.15s, color 0.15s; font-family: var(--cp-sans); }
.cp-tab:hover { color: var(--cp-navy); }
.cp-tab.on { background: #fff; color: var(--cp-navy); box-shadow: 0 1px 3px rgba(0, 0, 0, 0.16); }
.cp-panel { padding: 20px 0 0; }

/* grids */
.cp-grid { display: grid; gap: 18px; align-items: start; }
.cp-grid.g-25 { grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr); }
.cp-grid.g-52 { grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr); }
.cp-grid.g-2 { grid-template-columns: 1fr 1fr; }
@media (max-width: 960px) { .cp-grid.g-25, .cp-grid.g-52, .cp-grid.g-2 { grid-template-columns: 1fr; } }
.cp-col { display: flex; flex-direction: column; gap: 18px; }

/* definition rows */
.cp-dl { display: flex; flex-direction: column; margin: 0; }
.cp-dl > div { display: flex; align-items: baseline; gap: 16px; padding: 8px 0; border-bottom: 1px solid var(--cp-border-soft); }
.cp-dl > div:last-child { border-bottom: none; }
.cp-dl dt { flex: none; width: 46%; max-width: 220px; font-size: 12.5px; color: var(--cp-muted); }
.cp-dl dd { margin: 0; flex: 1; min-width: 0; font-size: 13.5px; font-weight: 500; color: var(--cp-text); overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.cp-dl dd.none { font-weight: 400; color: var(--cp-faint); font-style: italic; font-variant-numeric: normal; }
.cp-dl dd.mono { font-family: var(--cp-mono); font-size: 12.5px; }
.cp-dl dd a { color: var(--cp-sky-dark); font-weight: 600; }
@media (max-width: 520px) { .cp-dl > div { flex-direction: column; gap: 2px; } .cp-dl dt { width: auto; max-width: none; } }

.cp-sublabel { font-size: 11px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: var(--cp-faint); padding: 11px 0 7px; }

/* pills */
.cp-pills { display: flex; flex-wrap: wrap; gap: 6px; padding: 2px 0 4px; }
.cp-pill { display: inline-flex; align-items: center; font-size: 12px; font-weight: 500; padding: 4px 11px; border-radius: 14px; background: var(--cp-surface-2); border: 1px solid var(--cp-border-soft); color: var(--cp-text); }
.cp-pill.navy { background: var(--cp-navy-wash); border-color: var(--cp-navy-wash-br); color: var(--cp-navy); font-weight: 600; }

/* socials */
.cp-socials { display: flex; gap: 8px; padding: 6px 0 2px; flex-wrap: wrap; }
.cp-soc { display: inline-flex; align-items: center; gap: 7px; font-size: 12.5px; font-weight: 500; padding: 6px 12px; border-radius: 3px; border: 1px solid var(--cp-border); background: var(--cp-surface); color: var(--cp-text); transition: border-color 0.14s, background 0.14s, color 0.14s; }
.cp-soc:hover { border-color: var(--cp-sky); background: var(--cp-sky-wash); color: var(--cp-sky-dark); }
.cp-soc i { color: var(--cp-faint); font-size: 14px; }
.cp-soc:hover i { color: var(--cp-sky-dark); }

/* people */
.cp-people { display: flex; flex-direction: column; }
.cp-person { display: flex; align-items: center; gap: 12px; padding: 9px 0; border-bottom: 1px solid var(--cp-border-soft); }
.cp-person:last-child { border-bottom: none; }
.cp-avatar { width: 34px; height: 34px; border-radius: 50%; flex: none; background: var(--cp-navy-wash); color: var(--cp-navy); display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 600; }
.cp-person .nm { font-size: 13.5px; font-weight: 600; color: var(--cp-text); }
.cp-person .ti { font-size: 12px; color: var(--cp-muted); margin-top: 1px; }

/* credit */
.cp-credit { display: flex; align-items: center; gap: 18px; padding: 14px 0 6px; }
.cp-grade { width: 60px; height: 60px; flex: none; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 26px; font-weight: 700; background: var(--cp-surface-2); color: var(--cp-muted); border: 1px solid var(--cp-border-soft); }
.cp-grade.good { background: var(--cp-green-bg); color: var(--cp-green); border-color: var(--cp-green-br); }
.cp-grade.warn { background: var(--cp-amber-bg); color: var(--cp-amber); border-color: #f4dcb0; }
.cp-grade.bad { background: var(--cp-red-bg); color: var(--cp-red); border-color: var(--cp-red-br); }
.cp-credit-m { flex: 1; min-width: 0; }
.cp-credit-m .lbl { font-size: 10px; font-weight: 600; letter-spacing: 0.07em; text-transform: uppercase; color: var(--cp-faint); }
.cp-credit-m .sc { display: flex; align-items: baseline; gap: 7px; margin-top: 3px; }
.cp-credit-m .sc b { font-size: 20px; font-weight: 700; font-variant-numeric: tabular-nums; color: var(--cp-text); }
.cp-credit-m .sc span { font-size: 12px; color: var(--cp-muted); }
.cp-credit-m .meter { height: 6px; border-radius: 99px; background: var(--cp-surface-2); border: 1px solid var(--cp-border-soft); margin-top: 9px; overflow: hidden; }
.cp-credit-m .meter i { display: block; height: 100%; background: var(--cp-green); }

/* table */
.cp-tbl-wrap { overflow-x: auto; }
.cp-tbl { border-collapse: collapse; width: 100%; font-size: 12.5px; min-width: 520px; }
.cp-tbl th { text-align: left; font-size: 10.5px; letter-spacing: 0.05em; text-transform: uppercase; color: var(--cp-faint); font-weight: 600; padding: 8px 14px; border-bottom: 1px solid var(--cp-border); white-space: nowrap; background: var(--cp-surface-2); }
.cp-tbl td { padding: 9px 14px; border-bottom: 1px solid var(--cp-border-soft); vertical-align: top; font-variant-numeric: tabular-nums; color: var(--cp-text); }
.cp-tbl tbody tr:last-child td { border-bottom: none; }
.cp-tbl td b { font-weight: 600; }
.cp-tbl td .sub { font-size: 11px; color: var(--cp-muted); margin-top: 2px; font-variant-numeric: normal; }
.cp-tbl .num { text-align: right; }
.cp-plan-toggle { padding: 11px 20px; border-top: 1px solid var(--cp-border-soft); }
.cp-plan-toggle .md-icon { font-size: 16px !important; color: inherit !important; }
.cp-plan-detail { padding: 2px 20px 16px; border-top: 1px solid var(--cp-border-soft); }
.cp-plan-detail .cp-grid { padding-top: 14px; }

/* empty */
.cp-empty { display: flex; align-items: flex-start; gap: 12px; padding: 14px 0 10px; }
.cp-empty .ei { width: 32px; height: 32px; border-radius: 6px; flex: none; background: var(--cp-green-bg); color: var(--cp-green); display: flex; align-items: center; justify-content: center; }
.cp-empty .ei.neutral { background: var(--cp-surface-2); color: var(--cp-faint); }
.cp-empty .ei .md-icon { color: inherit !important; font-size: 18px !important; }
.cp-empty .et { font-size: 13.5px; font-weight: 600; color: var(--cp-text); }
.cp-empty .ed { font-size: 12.5px; color: var(--cp-muted); margin-top: 3px; line-height: 1.55; max-width: 52ch; }

/* map */
.cp-map { background: var(--cp-surface-2); }

/* note */
.cp-note { display: flex; gap: 10px; align-items: flex-start; margin-top: 12px; padding: 13px 16px; border-radius: var(--cp-radius); background: var(--cp-sky-wash); border: 1px solid var(--cp-sky-wash-br); font-size: 12.5px; color: #2a4a5c; line-height: 1.6; }
.cp-note .md-icon { color: var(--cp-sky-dark) !important; font-size: 16px !important; margin-top: 1px; flex: none; }
.cp-note b { color: var(--cp-navy); font-weight: 600; }
</style>
